import type {
    ConnectionDiag,
    ConnectionOptions,
    DatabaseDiagnostics,
    SqlParam,
    SyncEvent,
    SyncMode,
    SyncResult,
} from "./sqliteDatabase"
import type {
    Sqlite3MtClientAttachment,
    Sqlite3MtOptions,
} from "./sqliteEngine"
import type { FaultRequestMessage } from "./sqlite3mt/worker-bridge"
import type {
    Sqlite3MtDebugStagePayload,
    WalCommitSyncMessage,
} from "./sqlite3mt/types"
import type { SqliteBackgroundSyncMode, WorkspaceSyncPolicy } from "./types"
import type { SdkLoggingOptions } from "./logger"
import { base64UrlEncode } from "./workspaceSecretV2"

export interface BrowserSqliteOpenConfig {
    readonly apiBaseUrl: string
    readonly blobId: string
    readonly workspaceSecret: string
    readonly label: string
    readonly emoji: string
    readonly canWrite: boolean
    readonly path: string
    readonly useSqlite3Mt?: Sqlite3MtOptions
    readonly backgroundSync?: SqliteBackgroundSyncMode
    readonly syncPolicy?: WorkspaceSyncPolicy
    readonly logging?: SdkLoggingOptions
    /**
     * Internal browser-main assigned durable-local scope for sqlite3mt's
     * journal/snapshot/intent store. The remote sqlite3mt head remains
     * `(blobId,path)` scoped; this isolates WAL-local state per live runtime.
     */
    readonly sqlite3MtDurableScope?: string
}

export function buildBrowserSqliteWorkspaceName(blobId: string, path: string): string {
    return `${blobId}:${path}`
}

function normalizeBrowserSqliteCarrierPath(blobId: string, path: string): string {
    const duplicatedPrefix = `${blobId}:`
    if (path.startsWith(duplicatedPrefix)) {
        return path.slice(duplicatedPrefix.length)
    }
    return path
}

function encodeBrowserSqliteCarrierPath(blobId: string, path: string): string {
    const normalizedPath = normalizeBrowserSqliteCarrierPath(blobId, path)
    return base64UrlEncode(new TextEncoder().encode(normalizedPath))
}

export function buildBrowserSqliteHeadLane(blobId: string, path: string): string {
    return encodeBrowserSqliteCarrierPath(blobId, path)
}

export function buildBrowserSqliteHeadBlobId(blobId: string, path: string): string {
    return `${blobId}:sqlite3mt:${buildBrowserSqliteHeadLane(blobId, path)}`
}

export interface SerializedSqliteError {
    readonly name: string
    readonly message: string
    readonly stack?: string
    readonly code?: number
    readonly codeName?: string
}

export interface BrowserSqliteClientIoFatalMessage {
    readonly type: 'client-io-fatal'
    readonly runtimeEpoch: bigint
    readonly connId: number
}

export interface BrowserSqliteClientRequestLifecycleMessage {
    readonly type:
        | 'client-request-start'
        | 'client-request-end'
        | 'client-transaction-start'
        | 'client-transaction-end'
    readonly runtimeEpoch: bigint
    readonly connId: number
}

export interface BrowserSqliteClientSchemaLagMessage {
    readonly type: 'client-schema-lag'
    readonly runtimeEpoch: bigint
    readonly connId: number
    readonly seq: number
    readonly error?: SerializedSqliteError
}

export type BrowserSqliteClientToControlMessage =
    | FaultRequestMessage
    | WalCommitSyncMessage
    | BrowserSqliteClientIoFatalMessage
    | BrowserSqliteClientRequestLifecycleMessage
    | BrowserSqliteClientSchemaLagMessage

export interface BrowserSqliteControlSchemaLagResolvedMessage {
    readonly type: 'schema-lag-resolved'
    readonly runtimeEpoch: bigint
    readonly connId: number
    readonly seq: number
    readonly observedRemoteEpoch: number | null
    readonly applied: boolean
}

export type BrowserSqliteControlToClientMessage =
    | BrowserSqliteControlSchemaLagResolvedMessage

export type BrowserSqliteControlWorkerRequest =
    | { readonly kind: "request"; readonly requestId: number; readonly op: "init"; readonly config: BrowserSqliteOpenConfig }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-connect"; readonly options?: ConnectionOptions; readonly port: MessagePort }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-recover-client-init-failure"; readonly failedConnId: number }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-recover-client-io-failure"; readonly failedConnId: number }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-close"; readonly flushRemote?: boolean; readonly flushLocal?: boolean }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-flush"; readonly remote?: boolean; readonly wait?: boolean }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-client-request-start"; readonly connId: number; readonly runtimeEpoch: bigint }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-client-request-end"; readonly connId: number; readonly runtimeEpoch: bigint }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-set-sync-mode"; readonly mode: SyncMode }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-start-sync"; readonly interval?: number }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-stop-sync" }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-set-workspace-sync-policy"; readonly policy: WorkspaceSyncPolicy }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-sync-now" }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-diagnostics" }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "db-release-client"; readonly connId: number }

export type BrowserSqliteClientWorkerRequest =
    | { readonly kind: "request"; readonly requestId: number; readonly op: "init-connection"; readonly attach: Sqlite3MtClientAttachment; readonly port: MessagePort }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "conn-execute"; readonly connId: number; readonly sql: string; readonly params?: SqlParam[] }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "conn-close"; readonly connId: number }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "conn-diagnostics"; readonly connId: number }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "tx-begin"; readonly connId: number; readonly immediate: boolean }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "tx-execute"; readonly txId: number; readonly sql: string; readonly params?: SqlParam[] }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "tx-commit"; readonly txId: number }
    | { readonly kind: "request"; readonly requestId: number; readonly op: "tx-rollback"; readonly txId: number }

export type BrowserSqliteWorkerResponse =
    | { readonly kind: "response"; readonly requestId: number; readonly ok: true; readonly result?: unknown }
    | { readonly kind: "response"; readonly requestId: number; readonly ok: false; readonly error: SerializedSqliteError }

export type BrowserSqliteControlWorkerEvent =
    | { readonly kind: "sync-event"; readonly event: SyncEvent }
    | { readonly kind: "diagnostics"; readonly diag: DatabaseDiagnostics }
    | {
        readonly kind: "debug-stage"
        readonly payload: Sqlite3MtDebugStagePayload
    }

export interface BrowserSqliteInitResult {
    readonly syncMode: SyncMode
    readonly diagnostics: DatabaseDiagnostics
}

export interface BrowserSqliteConnectResult {
    readonly connId: number
    readonly diagnostics: ConnectionDiag
    readonly attach: Sqlite3MtClientAttachment
}

export interface BrowserSqliteSyncNowResult extends SyncResult {}
